/**
 * App Notifications Utility
 * Handles browser OS notifications + Web Audio API ding sound.
 */

// ── Sound ────────────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  return audioCtx;
}

/** Play a pleasant two-tone "ding" using Web Audio API — no file needed. */
export function playDing(): void {
  const ctx = getAudioContext();

  const doPlay = () => {
    try {
      const playTone = (frequency: number, startAt: number, duration: number, gain: number) => {
        const oscillator = ctx.createOscillator();
        const gainNode   = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + startAt);

        gainNode.gain.setValueAtTime(0, ctx.currentTime + startAt);
        gainNode.gain.linearRampToValueAtTime(gain, ctx.currentTime + startAt + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + duration);

        oscillator.start(ctx.currentTime + startAt);
        oscillator.stop(ctx.currentTime + startAt + duration);
      };

      // Two-tone ding: 880 Hz (A5) then 1109 Hz (C#6)
      playTone(880,  0,    0.35, 0.4);
      playTone(1109, 0.15, 0.4,  0.3);
    } catch {
      // Silently ignore
    }
  };

  // Resume suspended context first (browsers suspend AudioContext until user interaction)
  if (ctx.state === 'suspended') {
    ctx.resume().then(doPlay).catch(() => {});
  } else {
    doPlay();
  }
}

// ── Browser Notifications ─────────────────────────────────────────────────────

/** Request notification permission (call once after login). */
export async function requestNotificationPermission(): Promise<void> {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }
}

/** Show a welcome notification after login. */
export function showLoginNotification(fullName: string): void {
  playDing();

  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const notification = new Notification('✅ Countora', {
    body: `Welcome back, ${fullName}!`,
    icon: '/logo192.png',
    tag: 'countora-login',
    silent: true,
  });

  setTimeout(() => notification.close(), 4000);
}

/** Show an OS notification and play a ding sound. */
export function showChatNotification(senderName: string, body: string): void {
  // Always play sound (works even without notification permission)
  playDing();

  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const notification = new Notification(`💬 ${senderName}`, {
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: 'countora-chat',   // replaces previous notification instead of stacking
    silent: true,           // we handle the sound ourselves
  });

  // Auto-close after 5 seconds
  setTimeout(() => notification.close(), 5000);
}
